import { RoleConstant, ANNOUNCEMENT_ANY_AUTHOR_ROLE_IDS } from '../models/role-constant';
import { CompanyPageSubMenu } from '../models/company-permission.model';
import { isApListTabUrlAllowed } from './ap-list-tab.util';
import {
  isApListRoutePath,
  isApSubMenuAction,
  normalizePageUrl,
  normalizeRoutePath,
} from './company-permission.util';

/**
 * Sidebar role allowlists + always-allowed shell routes.
 * Keep in sync with md-shell nav in auth.layout.html.
 */

/** Routes every authenticated user may open (header/shell), regardless of sidebar module. */
export const ALWAYS_ALLOWED_ROUTE_PREFIXES: readonly string[] = [
  'meal-dynamics-ai',
  'profile',
  'notifications',
  'support',
  'ai-preferences',
];

export const REPAIR_SIDEBAR_ROLE_IDS: readonly string[] = [
  RoleConstant.globalAdminId,
  RoleConstant.companyAdminId,
  RoleConstant.srDirectorOperations,
  RoleConstant.directorOperations,
  RoleConstant.supervisor,
  RoleConstant.generalManager,
  RoleConstant.vpOperations,
  RoleConstant.employeeId,
  RoleConstant.vendorId,
];

/** Approval Process under R&M — same as repair except employee + vendor. */
export const APPROVAL_PROCESS_SIDEBAR_ROLE_IDS: readonly string[] =
  REPAIR_SIDEBAR_ROLE_IDS.filter(
    (id) => id !== RoleConstant.employeeId && id !== RoleConstant.vendorId
  );

export const ASSET_SIDEBAR_ROLE_IDS: readonly string[] = [
  RoleConstant.globalAdminId,
  RoleConstant.companyAdminId,
  RoleConstant.srDirectorOperations,
  RoleConstant.directorOperations,
  RoleConstant.supervisor,
  RoleConstant.generalManager,
  RoleConstant.vpOperations,
  RoleConstant.employeeId,
];

export const COMPANY_SIDEBAR_ROLE_IDS: readonly string[] = [
  RoleConstant.globalAdminId,
  RoleConstant.companyAdminId,
  RoleConstant.srDirectorOperations,
  RoleConstant.directorOperations,
  RoleConstant.supervisor,
  RoleConstant.generalManager,
  RoleConstant.vpOperations,
];

export const STORE_GROUP_SIDEBAR_ROLE_IDS: readonly string[] = [
  RoleConstant.globalAdminId,
  RoleConstant.companyAdminId,
  RoleConstant.vpOperations,
  RoleConstant.srDirectorOperations,
  RoleConstant.directorOperations,
  RoleConstant.supervisor,
  RoleConstant.generalManager,
];

export const STORE_SIDEBAR_ROLE_IDS: readonly string[] = [
  RoleConstant.globalAdminId,
  RoleConstant.companyAdminId,
  RoleConstant.srDirectorOperations,
  RoleConstant.directorOperations,
  RoleConstant.supervisor,
  RoleConstant.generalManager,
  RoleConstant.vpOperations,
];

export const USER_SIDEBAR_ROLE_IDS: readonly string[] = [
  RoleConstant.globalAdminId,
  RoleConstant.companyAdminId,
  RoleConstant.srDirectorOperations,
  RoleConstant.directorOperations,
  RoleConstant.supervisor,
  RoleConstant.generalManager,
  RoleConstant.vpOperations,
];

export const VENDOR_SIDEBAR_ROLE_IDS: readonly string[] = [
  RoleConstant.globalAdminId,
  RoleConstant.companyAdminId,
  RoleConstant.srDirectorOperations,
  RoleConstant.directorOperations,
  RoleConstant.supervisor,
  RoleConstant.generalManager,
  RoleConstant.accountsPayableManager1,
  RoleConstant.accountsPayableManager2,
  RoleConstant.vpOperations,
];

export type SidebarRouteModule =
  | 'shell'
  | 'ap'
  | 'repair'
  | 'approvalProcess'
  | 'expense'
  | 'asset'
  | 'company'
  | 'storeGroup'
  | 'store'
  | 'user'
  | 'roles'
  | 'vendors'
  | 'announcements'
  | 'integrationData'
  | 'organizationSettings'
  | 'unknown';

export function stripQueryAndHash(url: string): string {
  return url.split('?')[0].split('#')[0];
}

export function resolveSidebarRouteModule(url: string): SidebarRouteModule {
  const path = normalizeRoutePath(stripQueryAndHash(url));

  // Homepage: normalizeRoutePath('') / '/' → '/'
  if (!path || path === '/' || path === '') {
    return 'shell';
  }

  if (
    ALWAYS_ALLOWED_ROUTE_PREFIXES.some(
      (prefix) =>
        Boolean(prefix) &&
        (path === prefix || path.startsWith(`${prefix}/`))
    )
  ) {
    return 'shell';
  }

  if (path.startsWith('accounts-payable')) {
    return 'ap';
  }
  if (path.startsWith('repairs-and-maintenance')) {
    return 'repair';
  }
  if (
    path.startsWith('approval-processes') ||
    path.startsWith('approval-process')
  ) {
    return 'approvalProcess';
  }
  if (path.startsWith('expenses')) {
    return 'expense';
  }
  if (path.startsWith('asset-management')) {
    return 'asset';
  }
  // Before the Organizations module: the profile menu's own-organization page.
  if (path === 'companies/settings' || path.startsWith('companies/settings/')) {
    return 'organizationSettings';
  }
  if (path.startsWith('companies') || path.startsWith('company')) {
    return 'company';
  }
  if (path.startsWith('store-groups') || path.startsWith('store-group')) {
    return 'storeGroup';
  }
  if (path.startsWith('stores') || path.startsWith('store')) {
    return 'store';
  }
  if (path === 'roles' || path.startsWith('roles/')) {
    return 'roles';
  }
  if (path.startsWith('users')) {
    return 'user';
  }
  if (path.startsWith('vendors') || path.startsWith('vendor')) {
    return 'vendors';
  }
  if (path.startsWith('announcements')) {
    return 'announcements';
  }
  if (path.startsWith('integration-data')) {
    return 'integrationData';
  }

  return 'unknown';
}


function roleIn(roleId: string | null | undefined, allowed: readonly string[]): boolean {
  return Boolean(roleId && allowed.includes(roleId));
}

/**
 * Whether the selected role may open this URL per sidebar rules.
 * Accounts Payable uses API/legacy subMenus separately via {@link isAccountsPayableUrlAllowed}.
 */
export function isSidebarModuleAllowedForRole(
  module: SidebarRouteModule,
  roleId: string | null | undefined
): boolean {
  switch (module) {
    case 'shell':
      return true;
    case 'repair':
      return roleIn(roleId, REPAIR_SIDEBAR_ROLE_IDS);
    case 'approvalProcess':
      return roleIn(roleId, APPROVAL_PROCESS_SIDEBAR_ROLE_IDS);
    case 'expense':
      // Expense is BE pagePermissions-driven in RouteAccessGuard — not role allowlists.
      return false;
    case 'asset':
      return roleIn(roleId, ASSET_SIDEBAR_ROLE_IDS);
    case 'company':
      return roleIn(roleId, COMPANY_SIDEBAR_ROLE_IDS);
    case 'storeGroup':
      return roleIn(roleId, STORE_GROUP_SIDEBAR_ROLE_IDS);
    case 'store':
      return roleIn(roleId, STORE_SIDEBAR_ROLE_IDS);
    case 'user':
      return roleIn(roleId, USER_SIDEBAR_ROLE_IDS);
    case 'roles':
      // Until BE pagePermissions list roles/list: whoever sees Users (view-only unless they may edit roles).
      return roleIn(roleId, USER_SIDEBAR_ROLE_IDS);
    case 'vendors':
      return roleIn(roleId, VENDOR_SIDEBAR_ROLE_IDS);
    case 'announcements':
      return roleIn(roleId, ANNOUNCEMENT_ANY_AUTHOR_ROLE_IDS);
    case 'integrationData':
    case 'organizationSettings':
      // BE pagePermissions + Organization actionPermissions � handled by RouteAccessGuard.
      return false;
    case 'ap':
      // AP visibility comes from nav group / pagePermissions — handled by caller.
      return false;
    case 'unknown':
    default:
      return false;
  }
}

export type ApUrlAccessOptions = {
  /** True when user may open /accounts-payable/new (Create action). */
  allowCreate?: boolean;
  /** True when CollatedPaymentGuard would allow the route. */
  allowCollatedPayment?: boolean;
};

/**
 * Maps an AP URL to the sidebar pageUrl(s) that grant access.
 * Detail/view routes inherit from Accounts Payable List.
 */
export function isAccountsPayableUrlAllowed(
  url: string,
  subMenus: CompanyPageSubMenu[] | null | undefined,
  options: ApUrlAccessOptions = {}
): boolean {
  const path = normalizeRoutePath(stripQueryAndHash(url));
  if (!path.startsWith('accounts-payable')) {
    return false;
  }

  const menus = (subMenus ?? []).filter(
    (subMenu) => !isApSubMenuAction(subMenu.pageUrl)
  );
  const pageUrls = new Set(
    menus.map((subMenu) => normalizePageUrl(subMenu.pageUrl))
  );
  const hasList = pageUrls.has('accounts-payable/list');

  if (
    path === 'accounts-payable/collated-payment' ||
    path.startsWith('accounts-payable/collated-payment/')
  ) {
    return (
      pageUrls.has('accounts-payable/collated-payment') ||
      Boolean(options.allowCollatedPayment)
    );
  }

  if (path.includes('vendor-payment-list')) {
    return pageUrls.has('accounts-payable/vendor-payment-list');
  }

  if (path === 'accounts-payable/new' || path.endsWith('/new')) {
    return hasList || Boolean(options.allowCreate);
  }

  // Dashboard widget drilldown — lightweight generic listing (see widget-drilldown.util.ts).
  if (path === 'accounts-payable/overview') {
    return hasList || Boolean(options.allowCreate);
  }

  // list, view/:id, details/:id, and bare /accounts-payable
  if (isApListRoutePath(path)) {
    if (!hasList) {
      return false;
    }

    return isApListTabUrlAllowed(url, menus);
  }

  return false;
}
